import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Copy, FileText, Tag, Share2, Mail } from 'lucide-react';

type ContentType = 'blog_post' | 'meta_description' | 'social_media' | 'email_template';

export const ContentGenerator = () => {
  const [contentType, setContentType] = useState<ContentType>('blog_post');
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  
  // Blog post fields
  const [blogTopic, setBlogTopic] = useState('');
  const [blogKeywords, setBlogKeywords] = useState('');
  const [blogAudience, setBlogAudience] = useState('');
  
  // Meta description fields
  const [metaTitle, setMetaTitle] = useState('');
  const [metaContent, setMetaContent] = useState('');
  const [metaKeywords, setMetaKeywords] = useState('');
  
  // Social media fields
  const [socialTopic, setSocialTopic] = useState('');
  const [socialKeywords, setSocialKeywords] = useState('');
  const [socialPlatform, setSocialPlatform] = useState('');
  
  // Email template fields
  const [emailPurpose, setEmailPurpose] = useState('');
  const [emailAudience, setEmailAudience] = useState('');
  const [emailKeyPoints, setEmailKeyPoints] = useState('');

  const handleGenerate = async () => {
    setLoading(true);
    setGeneratedContent('');

    try {
      let input = {};
      
      switch (contentType) {
        case 'blog_post':
          if (!blogTopic.trim()) {
            toast.error('Please enter a blog topic');
            return;
          }
          input = {
            topic: blogTopic,
            keywords: blogKeywords,
            targetAudience: blogAudience,
          };
          break;
        case 'meta_description':
          if (!metaTitle.trim() || !metaContent.trim()) {
            toast.error('Please enter page title and content');
            return;
          }
          input = {
            title: metaTitle,
            content: metaContent,
            keywords: metaKeywords,
          };
          break;
        case 'social_media':
          if (!socialTopic.trim()) {
            toast.error('Please enter a topic');
            return;
          }
          input = {
            topic: socialTopic,
            keywords: socialKeywords,
            platform: socialPlatform,
          };
          break;
        case 'email_template':
          if (!emailPurpose.trim()) {
            toast.error('Please enter email purpose');
            return;
          }
          input = {
            purpose: emailPurpose,
            targetAudience: emailAudience,
            keyPoints: emailKeyPoints,
          };
          break;
      }

      const { data, error } = await supabase.functions.invoke('content-generator', {
        body: { contentType, input }
      });

      if (error) throw error;

      setGeneratedContent(data.content);
      toast.success('Content generated successfully!');
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate content');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedContent);
    toast.success('Copied to clipboard!');
  };

  const renderInputFields = () => {
    switch (contentType) {
      case 'blog_post':
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="blogTopic">Blog Topic *</Label>
              <Input
                id="blogTopic"
                placeholder="e.g., Benefits of Smart Toilets for Modern Homes"
                value={blogTopic}
                onChange={(e) => setBlogTopic(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="blogKeywords">Target Keywords (optional)</Label>
              <Input
                id="blogKeywords"
                placeholder="e.g., smart toilet, luxury bathroom, eco-friendly"
                value={blogKeywords}
                onChange={(e) => setBlogKeywords(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="blogAudience">Target Audience (optional)</Label>
              <Input
                id="blogAudience"
                placeholder="e.g., homeowners, interior designers, contractors"
                value={blogAudience}
                onChange={(e) => setBlogAudience(e.target.value)}
              />
            </div>
          </>
        );
      
      case 'meta_description':
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="metaTitle">Page Title *</Label>
              <Input
                id="metaTitle"
                placeholder="e.g., Luna M9 Smart Toilet - Premium Features"
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="metaContent">Page Content Summary *</Label>
              <Textarea
                id="metaContent"
                placeholder="Brief summary of what the page is about..."
                value={metaContent}
                onChange={(e) => setMetaContent(e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="metaKeywords">Target Keywords (optional)</Label>
              <Input
                id="metaKeywords"
                placeholder="e.g., luxury smart toilet, heated seat"
                value={metaKeywords}
                onChange={(e) => setMetaKeywords(e.target.value)}
              />
            </div>
          </>
        );
      
      case 'social_media':
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="socialTopic">Post Topic *</Label>
              <Input
                id="socialTopic"
                placeholder="e.g., New product launch, customer testimonial"
                value={socialTopic}
                onChange={(e) => setSocialTopic(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="socialPlatform">Platform (optional)</Label>
              <Select value={socialPlatform} onValueChange={setSocialPlatform}>
                <SelectTrigger>
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="twitter">Twitter/X</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="socialKeywords">Focus Keywords (optional)</Label>
              <Input
                id="socialKeywords"
                placeholder="e.g., innovation, sustainability, luxury"
                value={socialKeywords}
                onChange={(e) => setSocialKeywords(e.target.value)}
              />
            </div>
          </>
        );
      
      case 'email_template':
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="emailPurpose">Email Purpose *</Label>
              <Input
                id="emailPurpose"
                placeholder="e.g., Product introduction, Follow-up, Newsletter"
                value={emailPurpose}
                onChange={(e) => setEmailPurpose(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emailAudience">Target Audience (optional)</Label>
              <Input
                id="emailAudience"
                placeholder="e.g., B2B clients, distributors, end customers"
                value={emailAudience}
                onChange={(e) => setEmailAudience(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emailKeyPoints">Key Points to Include (optional)</Label>
              <Textarea
                id="emailKeyPoints"
                placeholder="List main points or features to highlight..."
                value={emailKeyPoints}
                onChange={(e) => setEmailKeyPoints(e.target.value)}
                rows={3}
              />
            </div>
          </>
        );
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>AI Content Generator</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="contentType">Content Type</Label>
            <Select value={contentType} onValueChange={(value) => setContentType(value as ContentType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blog_post">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Blog Post
                  </div>
                </SelectItem>
                <SelectItem value="meta_description">
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Meta Description
                  </div>
                </SelectItem>
                <SelectItem value="social_media">
                  <div className="flex items-center gap-2">
                    <Share2 className="h-4 w-4" />
                    Social Media Post
                  </div>
                </SelectItem>
                <SelectItem value="email_template">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email Template
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {renderInputFields()}

          <Button onClick={handleGenerate} disabled={loading} className="w-full">
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              'Generate Content'
            )}
          </Button>
        </CardContent>
      </Card>

      {generatedContent && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Generated Content</CardTitle>
            <Button variant="outline" size="sm" onClick={copyToClipboard}>
              <Copy className="h-4 w-4 mr-2" />
              Copy
            </Button>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-muted rounded-lg whitespace-pre-wrap">
              {generatedContent}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
